// Council mode data: server turn/speak calls (/api/ai/council) plus direct reads of the user's own
// council_sessions / council_messages rows (RLS: user_id = auth.uid()).
import { apiFetch } from '../lib/api';
import { supabase } from '../lib/supabase';
import { localDateKey } from '../live/screenContext';

export interface CouncilLine {
  id: string;
  speaker: string; // 'user' | 'syqi' | seat id
  content: string;
  kind: string; // 'message' | 'system'
  created_at: string;
  reply_to?: string | null;
}

export type CouncilStatus = 'active' | 'paused' | 'closed';
export interface CouncilSession {
  id: string;
  title: string;
  members: string[];
  muted: string[];
  status: CouncilStatus;
  updated_at: string;
}

export interface CouncilSettings {
  enabled: boolean;
  autoSpeak: boolean;
  replyMode: 'all' | 'addressed';
  maxSpeakers: number;
  defaultMembers: string[];
  voiceInput: boolean;
  syqiChairs: boolean;
}

// Mirrors DEFAULT_COUNCIL_SETTINGS in src/lib/agents/council.ts (server).
export const DEFAULT_COUNCIL: CouncilSettings = {
  enabled: true,
  autoSpeak: true,
  replyMode: 'all',
  maxSpeakers: 3,
  defaultMembers: [],
  voiceInput: true,
  syqiChairs: true,
};

export const withCouncilDefaults = (v: Partial<CouncilSettings> | null | undefined): CouncilSettings => ({ ...DEFAULT_COUNCIL, ...(v ?? {}) });

export async function councilTurn(p: { sessionId?: string; name?: string; members: string[]; muted: string[]; message: string }) {
  return apiFetch<{ sessionId: string; line: CouncilLine; speakers: string[]; paused?: boolean }>('/api/ai/council', {
    method: 'POST',
    body: JSON.stringify({ action: 'turn', today: localDateKey(), ...p }),
  });
}

export async function councilSpeak(sessionId: string, speaker: string) {
  return apiFetch<{ line: CouncilLine | null; wrote?: boolean; mentions?: string[] }>('/api/ai/council', {
    method: 'POST',
    body: JSON.stringify({ action: 'speak', today: localDateKey(), sessionId, speaker }),
  });
}

export async function listCouncilSessions(userId: string): Promise<CouncilSession[]> {
  const { data } = await supabase
    .from('council_sessions')
    .select('id, title, members, muted, status, updated_at')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false })
    .limit(40);
  return (data as CouncilSession[] | null) ?? [];
}

export async function loadCouncilLines(sessionId: string): Promise<CouncilLine[]> {
  const { data } = await supabase
    .from('council_messages')
    .select('id, speaker, content, kind, created_at, reply_to')
    .eq('session_id', sessionId)
    .order('created_at', { ascending: true })
    .limit(400);
  return (data as CouncilLine[] | null) ?? [];
}

/** Start a named room before the first line (Bothread's "Start room"). */
export async function createCouncilSession(userId: string, title: string, members: string[]): Promise<CouncilSession> {
  const { data, error } = await supabase
    .from('council_sessions')
    .insert({ user_id: userId, title: title.trim().slice(0, 80) || 'Council', members: members.filter(Boolean) })
    .select('id, title, members, muted, status, updated_at')
    .single();
  if (error) throw error;
  return data as CouncilSession;
}

export async function updateCouncilSession(id: string, patch: { title?: string; status?: CouncilStatus }): Promise<void> {
  await supabase.from('council_sessions').update({ ...patch, updated_at: new Date().toISOString() }).eq('id', id);
}

export async function deleteCouncilSession(id: string): Promise<void> {
  await supabase.from('council_sessions').delete().eq('id', id);
}

/** Persist roster changes (invite / remove / mute) on an existing session. Awaited: supabase builders are lazy. */
export async function saveCouncilRoster(id: string, members: string[], muted: string[]): Promise<void> {
  await supabase.from('council_sessions').update({ members, muted, updated_at: new Date().toISOString() }).eq('id', id);
}
